/** Country-aware Address create/edit form — P5 follow-up.
 *
 * Loads the per-country schema catalog from /admin/address-schemas/
 * once (queryClient caches it), then renders:
 *   - Country selector (drives the rest of the form)
 *   - Required + optional flat fields per the schema
 *   - country_specific.* fields (select / text)
 *   - Custom-field block resolved from /admin/fields/schema (applies_to
 *     = "address", scoped by country)
 *   - Postal-code regex validation client-side
 *
 * `address` prop: undefined → create form, set → edit form.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type TFn = ReturnType<typeof useTranslation>["t"];

import { CountrySelector } from "@/components/CountrySelector";
import { DEFAULT_COUNTRY } from "@/lib/countries";
import type {
  Address,
  AddressCountrySchema,
  AddressPurpose,
  CountrySpecificField,
  FieldDefinition,
} from "@/lib/types";
import { addressPurposeLabelKey } from "./addressLabels";
import {
  createContactAddress,
  loadAddressSchemas,
  patchContactAddress,
  resolveFieldSchema,
  type AddressWritePayload,
} from "./api";


type FormState = AddressWritePayload & {
  country_specific_jsonb: Record<string, string>;
  custom_fields_jsonb: Record<string, string>;
};

const PURPOSE_OPTIONS: AddressPurpose[] = [
  "beneficiary",
  "worksite",
  "billing",
  "other",
];


function _emptyForm(): FormState {
  return {
    purpose: "beneficiary",
    is_primary: false,
    label: "",
    street_type: "",
    street_name: "",
    street_number: "",
    sub_unit: "",
    locality: "",
    region: "",
    postal_code: "",
    country: DEFAULT_COUNTRY,
    notes: "",
    building_name: "",
    country_specific_jsonb: {},
    custom_fields_jsonb: {},
  };
}


function _seedFromAddress(addr: Address): FormState {
  const cs = addr.country_specific_jsonb || {};
  const cf = addr.custom_fields_jsonb || {};
  return {
    purpose: addr.purpose,
    is_primary: addr.is_primary,
    label: addr.label,
    street_type: addr.street_type,
    street_name: addr.street_name,
    street_number: addr.street_number,
    sub_unit: addr.sub_unit,
    locality: addr.locality,
    region: addr.region,
    postal_code: addr.postal_code,
    country: addr.country,
    notes: addr.notes,
    building_name: addr.building_name,
    country_specific_jsonb: Object.fromEntries(
      Object.entries(cs).map(([k, v]) => [k, String(v ?? "")]),
    ),
    custom_fields_jsonb: Object.fromEntries(
      Object.entries(cf).map(([k, v]) => [k, String(v ?? "")]),
    ),
  };
}


export function AddressForm({
  contactId,
  address,
  onSaved,
  onCancel,
  isBusiness,
}: {
  contactId: string;
  address?: Address;
  onSaved: (a: Address) => void;
  onCancel: () => void;
  /** When the owning Contact is a legal entity, the `beneficiary` purpose is
   *  labelled as the company's registered office. */
  isBusiness?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const schemas = useQuery({
    queryKey: ["address-schemas"],
    queryFn: loadAddressSchemas,
    staleTime: 1000 * 60 * 60, // 1h — catalog is effectively static
  });

  const [form, setForm] = useState<FormState>(() =>
    address ? _seedFromAddress(address) : _emptyForm(),
  );

  // Re-seed only when the row identity changes; downstream edits
  // are handled by cache invalidation in `save.onSuccess`.
  useEffect(() => {
    setForm(address ? _seedFromAddress(address) : _emptyForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.id]);

  const country = (form.country || "").toUpperCase();
  const schema: AddressCountrySchema = useMemo(() => {
    if (!schemas.data) {
      return {
        required: ["street_name", "locality", "country"],
        optional: [
          "street_type",
          "street_number",
          "sub_unit",
          "region",
          "postal_code",
          "building_name",
          "notes",
        ],
        country_specific: {},
      };
    }
    return (
      schemas.data[country] ||
      schemas.data._generic || {
        required: ["street_name", "locality", "country"],
        optional: [
          "street_type",
          "street_number",
          "sub_unit",
          "region",
          "postal_code",
          "building_name",
          "notes",
        ],
        country_specific: {},
      }
    );
  }, [schemas.data, country]);

  // Layer C custom fields scoped to Address + this country.
  const addressCfSchema = useQuery({
    queryKey: ["fields-schema", "address", country],
    queryFn: () =>
      resolveFieldSchema({ applies_to: "address", country }),
    enabled: !!country,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload: AddressWritePayload = {
        purpose: form.purpose,
        is_primary: form.is_primary,
        label: form.label || "",
        street_type: form.street_type || "",
        street_name: form.street_name || "",
        street_number: form.street_number || "",
        sub_unit: form.sub_unit || "",
        locality: form.locality || "",
        region: form.region || "",
        postal_code: form.postal_code || "",
        country: country,
        notes: form.notes || "",
        building_name: form.building_name || "",
        country_specific_jsonb: form.country_specific_jsonb,
        custom_fields_jsonb: form.custom_fields_jsonb,
      };
      if (address) {
        return patchContactAddress(contactId, address.id, payload);
      }
      return createContactAddress(contactId, payload);
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["contact", contactId] });
      void qc.invalidateQueries({ queryKey: ["contact-addresses", contactId] });
      onSaved(data);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  function patchField<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  function patchCs(key: string, v: string) {
    setForm((s) => ({
      ...s,
      country_specific_jsonb: { ...s.country_specific_jsonb, [key]: v },
    }));
  }

  function patchCf(key: string, v: string) {
    setForm((s) => ({
      ...s,
      custom_fields_jsonb: { ...s.custom_fields_jsonb, [key]: v },
    }));
  }

  // Validate postal code client-side against the schema's regex so the
  // operator gets feedback before the backend round-trip.
  const postalRegex = schema.postal_regex;
  const postalInvalid =
    postalRegex && form.postal_code
      ? !new RegExp(postalRegex).test(form.postal_code)
      : false;

  if (schemas.isLoading) {
    return (
      <div className="card text-sm text-nb-text-2">
        {t("common.loading", "Loading…")}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="card space-y-3 border-nb-mint/30 bg-nb-surface/30 p-4 text-sm"
    >
      <h3 className="text-base font-semibold">
        {address
          ? t("contacts.addresses.editTitle", "Edit address")
          : t("contacts.addresses.addTitle", "Add address")}
      </h3>

      <div className="grid grid-cols-2 gap-3">
        <label>
          <span className="block text-xs text-nb-text-2">
            {t("contacts.addresses.purpose", "Purpose")}
          </span>
          <select
            className="input mt-1 w-full"
            value={form.purpose}
            onChange={(e) =>
              patchField("purpose", e.target.value as AddressPurpose)
            }
          >
            {PURPOSE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {t(addressPurposeLabelKey(p, isBusiness), p)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="block text-xs text-nb-text-2">
            {t("contacts.addresses.country", "Country")} *
          </span>
          <CountrySelector
            className="mt-1"
            value={country}
            onChange={(code) => patchField("country", code)}
            aria-label={t("contacts.addresses.country", "Country")}
          />
        </label>

        <label className="col-span-2">
          <span className="block text-xs text-nb-text-2">
            {t("contacts.addresses.label", "Label (e.g. Main residence)")}
          </span>
          <input
            type="text"
            className="input mt-1 w-full"
            value={form.label || ""}
            onChange={(e) => patchField("label", e.target.value)}
          />
        </label>

        {_renderFlatField("street_type", schema, form, patchField, t)}
        {_renderFlatField("street_name", schema, form, patchField, t)}
        {_renderFlatField("street_number", schema, form, patchField, t)}
        {_renderFlatField("sub_unit", schema, form, patchField, t)}
        {_renderFlatField("building_name", schema, form, patchField, t)}
        {_renderFlatField(
          "postal_code",
          schema,
          form,
          patchField,
          t,
          postalInvalid,
        )}
        {_renderFlatField("locality", schema, form, patchField, t)}
        {_renderFlatField("region", schema, form, patchField, t)}

        {/* Country-specific fields (autonomous_community, province_code,
            department_code, bundesland, state, etc.) */}
        {Object.entries(schema.country_specific || {}).map(([key, def]) =>
          _renderCountrySpecific(
            key,
            def,
            form.country_specific_jsonb[key] || "",
            (v) => patchCs(key, v),
            t,
          ),
        )}

        <label className="col-span-2">
          <span className="block text-xs text-nb-text-2">
            {t("contacts.addresses.notes", "Notes")}
          </span>
          <textarea
            className="input mt-1 w-full"
            rows={2}
            value={form.notes || ""}
            onChange={(e) => patchField("notes", e.target.value)}
          />
        </label>
      </div>

      {/* Layer C custom fields scoped to addresses + this country. */}
      {addressCfSchema.data && addressCfSchema.data.length > 0 && (
        <div className="space-y-2 border-t border-nb-border pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.addresses.customFieldsHeading", "Custom fields")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {addressCfSchema.data.map((fd) =>
              _renderCustomField(
                fd,
                form.custom_fields_jsonb[fd.key] || "",
                (v) => patchCf(fd.key, v),
                t,
              ),
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2">
        <label className="flex items-center gap-2 text-xs text-nb-text-2">
          <input
            type="checkbox"
            checked={!!form.is_primary}
            onChange={(e) => patchField("is_primary", e.target.checked)}
          />
          {t("contacts.addresses.isPrimary", "Primary address for this purpose")}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onCancel}
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={save.isPending || postalInvalid}
          >
            {save.isPending
              ? t("common.saving", "Saving…")
              : t("common.save", "Save")}
          </button>
        </div>
      </div>

      {save.isError && (
        <p className="text-xs text-cvp-danger">{String(save.error)}</p>
      )}
    </form>
  );
}


// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

function _flatFieldVisible(
  field: string,
  schema: AddressCountrySchema,
): { required: boolean; visible: boolean } {
  const required = schema.required.includes(field);
  const optional = schema.optional.includes(field);
  return { required, visible: required || optional };
}


function _renderFlatField(
  field: string,
  schema: AddressCountrySchema,
  form: FormState,
  patchField: <K extends keyof FormState>(k: K, v: FormState[K]) => void,
  t: TFn,
  invalid = false,
) {
  const { required, visible } = _flatFieldVisible(field, schema);
  if (!visible) return null;
  const cs = schema.country_specific?.[field];
  // Some catalogs override a flat field with a country_specific select
  // (e.g. street_type for ES + IT). Render the select shape instead.
  if (cs?.kind === "select" && Array.isArray(cs.choices)) {
    return (
      <label key={field}>
        <span className="block text-xs text-nb-text-2">
          {t(`contacts.addresses.${field}`, field)}
          {required && " *"}
        </span>
        <select
          className="input mt-1 w-full"
          value={(form[field as keyof FormState] as string) || ""}
          onChange={(e) =>
            patchField(field as keyof FormState, e.target.value as never)
          }
        >
          <option value="">—</option>
          {(cs.choices ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <label key={field}>
      <span className="block text-xs text-nb-text-2">
        {t(`contacts.addresses.${field}`, field)}
        {required && " *"}
      </span>
      <input
        type="text"
        className={`input mt-1 w-full ${invalid ? "border-cvp-danger" : ""}`}
        value={(form[field as keyof FormState] as string) || ""}
        onChange={(e) =>
          patchField(field as keyof FormState, e.target.value as never)
        }
      />
      {invalid && (
        <span className="text-[10px] text-cvp-danger">
          {t(
            "contacts.addresses.postalShapeError",
            "doesn't match the postal code shape for this country",
          )}
        </span>
      )}
    </label>
  );
}


function _renderCountrySpecific(
  key: string,
  def: CountrySpecificField,
  value: string,
  onChange: (v: string) => void,
  t: TFn,
) {
  // Street type lives in the typed `street_type` column, not JSONB —
  // skip rendering twice when the country catalog hoists it.
  if (key === "street_type") return null;
  const label =
    (def.label_en as string) ||
    String(def[`label_${key.split("_")[0]}`] || key.replace(/_/g, " "));
  if (def.kind === "select") {
    return (
      <label key={key}>
        <span className="block text-xs text-nb-text-2">
          {t(`contacts.addresses.cs_${key}`, label)}
          {def.required && " *"}
        </span>
        <select
          className="input mt-1 w-full"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(def.choices ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <label key={key}>
      <span className="block text-xs text-nb-text-2">
        {t(`contacts.addresses.cs_${key}`, label)}
        {def.required && " *"}
      </span>
      <input
        type="text"
        className="input mt-1 w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}


function _renderCustomField(
  fd: FieldDefinition,
  value: string,
  onChange: (v: string) => void,
  t: TFn,
) {
  const label = fd.label + (fd.is_required ? " *" : "");
  const help = fd.help_text;
  if (fd.kind === "dropdown" && fd.options_jsonb) {
    return (
      <label key={fd.id} className="col-span-1">
        <span className="block text-xs text-nb-text-2">{label}</span>
        <select
          className="input mt-1 w-full"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {fd.options_jsonb.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label || opt.value}
            </option>
          ))}
        </select>
        {help && <span className="text-[10px] text-nb-text-3">{help}</span>}
      </label>
    );
  }
  if (fd.kind === "boolean") {
    return (
      <label key={fd.id} className="col-span-1 flex items-center gap-2">
        <input
          type="checkbox"
          checked={value === "true" || value === "yes"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
        />
        <span className="text-xs">{label}</span>
      </label>
    );
  }
  return (
    <label key={fd.id} className="col-span-1">
      <span className="block text-xs text-nb-text-2">{label}</span>
      <input
        type={fd.kind === "number" ? "number" : fd.kind === "date" ? "date" : "text"}
        className="input mt-1 w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={fd.regex_pattern || undefined}
      />
      {help && <span className="text-[10px] text-nb-text-3">{help}</span>}
      {t(`contacts.addresses.cfKey_${fd.key}`, "") /* i18n slot */}
    </label>
  );
}
